import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal, Spinner } from '@quyva/shared-ui';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';

/**
 * OneDrive File Picker v8 (embedded via iframe).
 *
 * Replaces the deprecated OneDrive.js v7.2 popup picker, which no longer works
 * because Microsoft's login popup now sends a Cross-Origin-Opener-Policy header
 * that severs the SDK's popup handshake. v8 is hosted at the tenant's SharePoint
 * origin and communicates over a MessageChannel, so it needs no popup.
 *
 * Flow: the parent signs in once with MSAL (Graph token, for the backend). We
 * then (1) discover the tenant SharePoint base URL from Graph /me/drive,
 * (2) acquire a SharePoint-resource token silently, (3) POST the picker config
 * into the iframe, and (4) answer the picker's token / pick / close commands.
 *
 * Docs: https://learn.microsoft.com/onedrive/developer/controls/file-pickers/
 */

// SharePoint tokens use the "{resource}/.default" scope form.
const dotDefault = (resource) =>
    `${String(resource).replace(/\/+$/, '')}/.default`;

const newChannelId = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    const b = window.crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
    return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
        .slice(6, 8)
        .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
};

export const OneDrivePickerV8Modal = ({
    msalInstance,
    account,
    graphToken,
    onPicked,
    onClose,
}) => {
    const iframeRef = useRef(null);
    const portRef = useRef(null);
    const channelIdRef = useRef(newChannelId());
    const baseUrlRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Acquire a SharePoint-resource token for the picker. Silent first (reuses
    // the session established by the parent's login); popup only if the tenant
    // hasn't consented the SharePoint permissions yet.
    const getToken = async (resource) => {
        const scopes = [dotDefault(resource || baseUrlRef.current)];
        try {
            const resp = await msalInstance.acquireTokenSilent({
                account,
                scopes,
            });
            return resp.accessToken;
        } catch (e) {
            const resp = await msalInstance.acquireTokenPopup({
                account,
                scopes,
            });
            return resp.accessToken;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const channelMessageListener = async (message) => {
            const payload = message.data;

            if (payload.type === 'notification') {
                if (payload.data?.notification === 'page-loaded' && !cancelled) {
                    setLoading(false);
                }
                return;
            }

            if (payload.type !== 'command' || !portRef.current) {
                return;
            }

            // Every command must be acknowledged first.
            portRef.current.postMessage({
                type: 'acknowledge',
                id: message.data.id,
            });

            const command = payload.data;

            switch (command.command) {
                case 'authenticate': {
                    try {
                        const token = await getToken(command.resource);
                        if (!token) {
                            throw new Error('Unable to obtain a token.');
                        }
                        portRef.current.postMessage({
                            type: 'result',
                            id: message.data.id,
                            data: { result: 'token', token },
                        });
                    } catch (err) {
                        portRef.current.postMessage({
                            type: 'result',
                            id: message.data.id,
                            data: {
                                result: 'error',
                                error: {
                                    code: 'unableToObtainToken',
                                    message: err.message,
                                },
                            },
                        });
                    }
                    break;
                }
                case 'pick': {
                    try {
                        const item = (command.items || [])[0];
                        if (!item) {
                            throw new Error('No item selected.');
                        }
                        portRef.current.postMessage({
                            type: 'result',
                            id: message.data.id,
                            data: { result: 'success' },
                        });
                        onPicked({
                            id: item.id,
                            name: item.name || 'Selected folder',
                            folder: item.folder || {},
                            driveId: item.parentReference?.driveId,
                            sharePointEndpoint: item['@sharePoint.endpoint'],
                        });
                    } catch (err) {
                        portRef.current.postMessage({
                            type: 'result',
                            id: message.data.id,
                            data: {
                                result: 'error',
                                error: {
                                    code: 'unusableItem',
                                    message: err.message,
                                },
                            },
                        });
                    }
                    break;
                }
                case 'close': {
                    onClose();
                    break;
                }
                default: {
                    portRef.current.postMessage({
                        type: 'result',
                        id: message.data.id,
                        data: {
                            result: 'error',
                            error: {
                                code: 'unsupportedCommand',
                                message: command.command,
                            },
                        },
                    });
                    break;
                }
            }
        };

        const windowMessageHandler = (event) => {
            const win = iframeRef.current?.contentWindow;
            if (!win || event.source !== win) {
                return;
            }
            const message = event.data;
            if (
                message?.type === 'initialize' &&
                message.channelId === channelIdRef.current
            ) {
                portRef.current = event.ports[0];
                portRef.current.addEventListener(
                    'message',
                    channelMessageListener
                );
                portRef.current.start();
                portRef.current.postMessage({ type: 'activate' });
            }
        };

        const launch = async () => {
            try {
                // 1) Discover the tenant SharePoint base URL from Graph.
                const driveResp = await axios.get(
                    'https://graph.microsoft.com/v1.0/me/drive?$select=webUrl',
                    { headers: { Authorization: `Bearer ${graphToken}` } }
                );
                baseUrlRef.current = new URL(driveResp.data.webUrl).origin;

                // 2) Initial SharePoint token for the form POST.
                const accessToken = await getToken(baseUrlRef.current);
                if (cancelled || !iframeRef.current) {
                    return;
                }

                // 3) Build the picker config (folder picker, single select).
                const options = {
                    sdk: '8.0',
                    entry: { oneDrive: {} },
                    authentication: {},
                    messaging: {
                        origin: window.location.origin,
                        channelId: channelIdRef.current,
                    },
                    typesAndSources: {
                        mode: 'folders',
                        pivots: { oneDrive: true },
                    },
                    selection: { mode: 'single' },
                    commands: { pick: { action: 'select' } },
                };

                window.addEventListener('message', windowMessageHandler);

                // 4) POST the config into the (initially about:blank) iframe;
                // form.submit() navigates it to the picker page.
                const win = iframeRef.current.contentWindow;
                const queryString = new URLSearchParams({
                    filePicker: JSON.stringify(options),
                    locale: 'en-us',
                });
                const url = `${baseUrlRef.current}/_layouts/15/FilePicker.aspx?${queryString}`;
                const form = win.document.createElement('form');
                form.setAttribute('action', url);
                form.setAttribute('method', 'POST');
                const tokenInput = win.document.createElement('input');
                tokenInput.setAttribute('type', 'hidden');
                tokenInput.setAttribute('name', 'access_token');
                tokenInput.setAttribute('value', accessToken);
                form.appendChild(tokenInput);
                win.document.body.append(form);
                form.submit();
            } catch (err) {
                console.error('OneDrive Picker v8 launch error:', err);
                if (!cancelled) {
                    setError(
                        err?.message || 'Failed to open the OneDrive picker.'
                    );
                }
            }
        };

        launch();

        return () => {
            cancelled = true;
            window.removeEventListener('message', windowMessageHandler);
            if (portRef.current) {
                try {
                    portRef.current.close();
                } catch (e) {
                    /* no-op */
                }
                portRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal
            title='Select a OneDrive folder'
            titleIcon={faMicrosoft}
            onClose={onClose}
            className='w-[70rem] max-w-[95vw]'
            bodyClassName='w-full h-[70vh] bg-backgrounds-white border rounded-xl overflow-hidden relative'
        >
            {error ? (
                <div className='p-6 text-center text-buttons-alert400'>
                    {error}
                </div>
            ) : (
                <>
                    {loading && (
                        <div className='absolute inset-0 flex items-center justify-center bg-backgrounds-white z-10'>
                            <Spinner loadingText='Loading OneDrive…' />
                        </div>
                    )}
                    <iframe
                        ref={iframeRef}
                        title='OneDrive File Picker'
                        className='w-full h-full border-0'
                    />
                </>
            )}
        </Modal>
    );
};
